import { useCallback, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowLeft, Plus } from 'lucide-react'
import { Hoja } from '../components/Hoja'
import { Boton, Cabecera, Campo, Tarjeta, Vacio } from '../components/ui'
import { api } from '../lib/api'
import { useIrA } from '../lib/navegacion'

interface Hogar {
  id: string
  name: string
  members: Array<{ username: string; displayName: string }>
}

const NUEVO = 'nuevo'

/**
 * Alta de usuarios, sólo para el administrador. Necesita red: crear una
 * cuenta no es algo que pueda esperar en una cola de salida.
 */
export function Administracion() {
  const irA = useIrA()
  const [hogares, setHogares] = useState<Hogar[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creando, setCreando] = useState(false)
  const [creado, setCreado] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    try {
      const data = await api.get<{ households: Hogar[] }>('/api/admin/households')
      setHogares(data.households)
      setError(null)
    } catch (caught) {
      setError((caught as Error).message)
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  return (
    <>
      <button
        type="button"
        onClick={() => irA('ajustes')}
        className="label-caps -ml-1 mb-3 inline-flex min-h-11 items-center gap-1 px-1 text-muted-foreground outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Ajustes
      </button>

      <Cabecera titulo="Usuarios" subtitulo="Da de alta a alguien en un hogar, o en uno nuevo." />

      {creado && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 rounded-2xl bg-filled px-4 py-3 text-sm text-sage-deep"
        >
          Listo: <strong>{creado}</strong> ya puede entrar con la contraseña que le has puesto, y
          cambiarla desde Ajustes.
        </motion.p>
      )}

      {error && <p className="mt-5 text-sm text-destructive">{error}</p>}

      <div className="mt-5 space-y-3">
        {hogares === null && !error && <Vacio>Cargando…</Vacio>}
        {hogares?.map((hogar) => (
          <Tarjeta key={hogar.id}>
            <h2 className="font-display text-xl">{hogar.name}</h2>
            <ul className="mt-2 space-y-1">
              {hogar.members.map((member) => (
                <li key={member.username} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{member.displayName}</span>
                  <span className="shrink-0 text-muted-foreground">{member.username}</span>
                </li>
              ))}
            </ul>
          </Tarjeta>
        ))}
      </div>

      <Boton variante="principal" ancho className="mt-5" disabled={!hogares} onClick={() => setCreando(true)}>
        <Plus className="h-4 w-4" strokeWidth={2.4} />
        Nuevo usuario
      </Boton>

      <AnimatePresence>
        {creando && hogares && (
          <NuevoUsuario
            key="nuevo"
            hogares={hogares}
            onCerrar={() => setCreando(false)}
            onCreado={(username) => {
              setCreando(false)
              setCreado(username)
              void cargar()
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}

function NuevoUsuario({
  hogares,
  onCerrar,
  onCreado,
}: {
  hogares: Hogar[]
  onCerrar: () => void
  onCreado: (username: string) => void
}) {
  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [hogar, setHogar] = useState(hogares[0]?.id ?? NUEVO)
  const [nombreHogar, setNombreHogar] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const valido =
    displayName.trim() &&
    username.trim().length >= 3 &&
    password.length >= 8 &&
    (hogar !== NUEVO || nombreHogar.trim())

  async function crear(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (!valido) return
    setEnviando(true)
    setError(null)
    try {
      const data = await api.post<{ username: string }>('/api/admin/users', {
        displayName: displayName.trim(),
        username: username.trim(),
        password,
        ...(hogar === NUEVO ? { householdName: nombreHogar.trim() } : { householdId: hogar }),
      })
      onCreado(data.username)
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Hoja titulo="Nuevo usuario" onCerrar={onCerrar}>
      <form onSubmit={crear}>
        {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

        <Campo id="admin-nombre" label="Nombre">
          <input
            id="admin-nombre"
            className="campo"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoFocus
          />
        </Campo>
        <Campo id="admin-usuario" label="Usuario (mínimo 3 caracteres)">
          <input
            id="admin-usuario"
            className="campo"
            value={username}
            autoCapitalize="none"
            autoCorrect="off"
            onChange={(event) => setUsername(event.target.value)}
          />
        </Campo>
        <Campo id="admin-clave" label="Contraseña provisional (mínimo 8)">
          <input
            id="admin-clave"
            className="campo"
            type="text"
            autoComplete="off"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Campo>
        <Campo id="admin-hogar" label="Hogar">
          <select id="admin-hogar" className="campo" value={hogar} onChange={(event) => setHogar(event.target.value)}>
            {hogares.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
            <option value={NUEVO}>Hogar nuevo…</option>
          </select>
        </Campo>
        {hogar === NUEVO && (
          <Campo id="admin-nombre-hogar" label="Nombre del hogar nuevo">
            <input
              id="admin-nombre-hogar"
              className="campo"
              value={nombreHogar}
              onChange={(event) => setNombreHogar(event.target.value)}
            />
          </Campo>
        )}

        <Boton type="submit" variante="principal" ancho className="mt-2" disabled={!valido || enviando}>
          Crear usuario
        </Boton>
      </form>
    </Hoja>
  )
}
